// Walk-in (UC-APPOINTMENTS-008; INT-78, PAT-35, PAT-51, Q-04): a patient who arrives without a booking is put on the day's
// list — found by typeahead, with a type and, optionally, who will see them ("Seen by") and notes. The API records it Arrived
// now, in the branch shown, never in Zoho Bookings (D23). A Prohibited or Dormant patient is recorded with the status
// acknowledged here.
import type { AppointmentView, PatientSearchResult, UserRef } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { appointments, appointmentTypes, patientLookup } from "../api/endpoints.appointments";
import { describeError, isApiError } from "../api/problem";
import { formatTime } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Checkbox, Field, Modal, Select, TextArea, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";

/** The Walk-in action for Today and the Appointments header: the button and its form. */
export function WalkInButton({ branchId, staff = [], onDone }: { branchId?: string | undefined; staff?: readonly UserRef[]; onDone: (n: ReactNode) => void }) {
  const s = useSessionView();
  const block = useWriteBlock();
  const [open, setOpen] = useState(false);
  if (!can(s, "appointments.appointment.write")) return null;
  // The branch the walk-in is recorded at — the page's, or the session's active one; with neither there is nowhere to record it
  // (review, 4 Oct 2026).
  const at = branchId ?? s?.activeBranchId ?? undefined;
  return (
    <>
      <button className="btn" type="button" disabled={block !== null || !at} title={block ? WRITE_BLOCK_TITLE[block] : !at ? "Choose a branch first" : "Record a patient who arrived without a booking"} onClick={() => setOpen(true)}><Icon name="plus" />Walk-in</button>
      {open && <WalkInModal branchId={branchId} staff={staff} onClose={() => setOpen(false)} onDone={(n) => { setOpen(false); onDone(n); }} />}
    </>
  );
}

function WalkInModal({ branchId, staff, onClose, onDone }: { branchId?: string | undefined; staff: readonly UserRef[]; onClose: () => void; onDone: (n: ReactNode) => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [patient, setPatient] = useState<PatientSearchResult | null>(null);
  const [typeId, setTypeId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [notes, setNotes] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [key] = useState(() => newIdempotencyKey());
  const search = useQuery({ queryKey: ["patient-search", q], queryFn: () => patientLookup.search(q), enabled: patient === null && q.trim().length >= 2 });
  const types = useQuery({ queryKey: ["appointment-types"], queryFn: () => appointmentTypes.list() });
  const activeTypes = (types.data?.data.items ?? []).filter((t) => t.isActive);
  const flagged = patient !== null && (patient.status === "Prohibited" || patient.status === "Dormant");
  const ready = patient !== null && patient.status !== "Archived" && typeId !== "" && (!flagged || ack) && !busy;
  const fieldErr = (path: string) => (isApiError(error) ? error.field(path)?.message : undefined);
  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await appointments.walkIn({
        patientId: patient.id as never, typeId: typeId as never, source: "walk_in",
        ...(staffId ? { optometristId: staffId as never } : {}), ...(notes.trim() ? { notes: notes.trim() } : {}), ...(flagged ? { acknowledgePatientStatus: true } : {}),
      }, key, branchId);
      void qc.invalidateQueries({ queryKey: ["today"] });
      void qc.invalidateQueries({ queryKey: ["calendar"] });
      onDone(walkInNotice(data));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Walk-in" meta="recorded as arrived now; not sent to Zoho Bookings" onClose={onClose} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn primary" type="submit" disabled={!ready}>{busy ? "Recording…" : "Record walk-in"}</button>
    </>}>
      {error !== null && !(isApiError(error) && error.code === "VALIDATION_FAILED" && error.errors.every((e) => ["optometristId", "typeId", "notes"].includes(e.path))) && <Alert tone="crit"><b>Could not record the walk-in.</b> {isApiError(error) && error.code === "PATIENT_NOT_TRANSACTABLE" ? "The patient is archived; un-archive the record first." : describeError(error)}</Alert>}
      {patient ? (
        <Field label="Patient" required>
          <div className="row c gap-8"><span className="grow"><b>{patient.displayName}</b> <span className="mono faint">{patient.accountCode}</span> · {patient.status}</span><button className="btn sm" type="button" onClick={() => { setPatient(null); setAck(false); }}>Change</button></div>
        </Field>
      ) : (
        <>
          <Field label="Patient" required htmlFor="walkin-search" help={<>Name, account code, mobile or email — two characters to search. A new patient? <Link to="/patients/new">Register them first</Link>.</>}>
            <TextInput id="walkin-search" value={q} autoFocus onChange={(e) => setQ(e.target.value)} placeholder="Search patients" />
          </Field>
          {search.isError && <Alert tone="crit">{describeError(search.error)}</Alert>}
          {search.data && (search.data.data.items.length === 0 ? <span className="help">No patient matches.</span> : (
            <div className="list bordered">
              {search.data.data.items.map((p) => (
                <div key={p.id} className="item">
                  <div className="grow"><div className="primary">{p.displayName} <span className="mono faint">{p.accountCode}</span></div><div className="sub">{p.status}{p.mobile ? ` · ${p.mobile}` : ""}</div></div>
                  <button className="btn sm" type="button" disabled={p.status === "Archived"} title={p.status === "Archived" ? "Archived patients cannot be booked" : undefined} onClick={() => setPatient(p)}>Choose</button>
                </div>
              ))}
            </div>
          ))}
        </>
      )}
      {flagged && (
        <Alert tone={patient!.status === "Prohibited" ? "crit" : "warn"}>
          <b>{patient!.displayName} is {patient!.status}.</b> {patient!.status === "Prohibited" ? "No quote, order or invoice can be made until the account is reactivated." : "The account has had no visit for a long time; reactivate it from the record after the visit."}
          <div><Checkbox checked={ack} onChange={setAck}>Record the walk-in anyway</Checkbox></div>
        </Alert>
      )}
      <Field label="Type" required htmlFor="walkin-type" error={fieldErr("typeId")}>
        <Select id="walkin-type" value={typeId} onChange={(e) => setTypeId(e.target.value)}><option value="">—</option>{activeTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</Select>
      </Field>
      {/* "Seen by", as on the Edit, the appointment's panel, Today and the patient's tab: "With" alone was not understood (DVN's
          test checklist of Sprints 1 and 2, note H2). */}
      {staff.length > 0 && (
        <Field label="Seen by" htmlFor="walkin-staff" error={fieldErr("optometristId")} help="Optional: who will see the patient. Leave Unassigned to choose later.">
          <Select id="walkin-staff" value={staffId} onChange={(e) => setStaffId(e.target.value)}><option value="">Unassigned</option>{staff.map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}</Select>
        </Field>
      )}
      <Field label="Notes" htmlFor="walkin-notes" error={fieldErr("notes")}><TextArea id="walkin-notes" value={notes} maxLength={2000} rows={2} onChange={(e) => setNotes(e.target.value)} placeholder="Wants prescription sunglasses" /></Field>
    </Modal>
  );
}

function walkInNotice(a: AppointmentView): ReactNode {
  return (
    <Alert tone={a.warnings.length > 0 ? "warn" : "ok"}>
      <b>Walk-in recorded: {a.patient.displayName}, {a.appointmentType.name} at {formatTime(a.startAt)}.</b>{a.warnings.length > 0 ? ` ${a.warnings.map((w) => w.message).join(" ")}` : ""}
    </Alert>
  );
}
